import { memo, useCallback, useId, useRef } from 'react';
import { Badge, Button, Card } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { PERMISSIONS } from '../../../constants/permissions.js';
import { ISSUE } from '../../../constants/reviewFilters.js';
import { ADMIN_CHOICE, DOMAIN_CHOICE, DUPLICATE_KIND } from '../../../constants/inviteModes.js';
import { usePermission } from '../../../hooks/usePermission.js';
import { personDisplayName } from '../../../utils/personDisplayName.js';
import BlockerPerson from './BlockerPerson.jsx';
import DuplicateCard from './DuplicateCard.jsx';

/**
 * "Before you add them": Admins to confirm, possible duplicates and people outside the
 * workspace domain. Everything must be handled before the primary button enables.
 *
 * @param {object} props
 * @param {{ admins: object[], duplicates: object[], outsideDomain: object[], total: number }} props.blockers
 * @param {{ rows: object[], catalog: { members: object[], domain: string } }} props.context
 * @param {(rowNumber: number, resolution: string) => void} props.onDuplicate
 * @param {(rowNumber: number, choice: 'confirm'|'member') => void} props.onAdmin
 * @param {(rowNumber: number, choice: 'keep'|'remove') => void} props.onOutsideDomain
 */
function BeforeYouAddChecklist({ blockers, context, onDuplicate, onAdmin, onOutsideDomain }) {
  const { t } = useTranslation('people-access');
  // Making someone Admin is the user-type permission: without it the Admin of the file cannot be
  // confirmed, only changed to Member.
  const { allowed: canMakeAdmin, loading: loadingPermission } = usePermission(PERMISSIONS.MEMBERS_CHANGE_USER_TYPE);
  const notAllowedId = useId();
  const headingRef = useRef(null);
  // A handled row disappears with the button that was pressed: keep the focus in the checklist.
  const keepFocus = useCallback(
    (handler) =>
      (...args) => {
        handler(...args);
        headingRef.current?.focus();
      },
    [],
  );
  const handleAdmin = keepFocus(onAdmin);
  const handleDuplicate = keepFocus(onDuplicate);
  const handleOutsideDomain = keepFocus(onOutsideDomain);
  const { admins, duplicates, outsideDomain, keptOutside, total } = blockers;
  const { rows, catalog } = context;

  const otherOf = (issue) =>
    issue.kind === DUPLICATE_KIND.MEMBER
      ? catalog.members.find((m) => m.id === issue.memberId)
      : rows.find((r) => r.rowNumber === issue.otherRowNumber)?.person;

  return (
    <Card className="flex flex-col gap-4 p-5">
      <div className="flex items-center gap-2">
        <h2 ref={headingRef} tabIndex={-1} className="text-base font-bold text-content focus:outline-none">
          {t('import.check.before.title')}
        </h2>
        <span role="status">
          <Badge size="small" variant={total > 0 ? 'warning' : 'success'}>
            {total > 0 ? t('import.check.before.toReview', { count: total }) : t('import.check.before.allReviewed')}
          </Badge>
        </span>
      </div>
      {total === 0 ? (
        <p className="text-sm text-content-secondary">{t('import.check.before.allSetNote')}</p>
      ) : (
        <p className="text-sm text-content-secondary">{t('import.check.before.intro')}</p>
      )}

      {admins.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-content">{t('import.check.before.admins', { count: admins.length })}</h3>
          <p className="text-xs text-content-secondary">{t('import.check.before.adminsHint')}</p>
          {canMakeAdmin || loadingPermission ? null : (
            <p id={notAllowedId} className="text-xs text-content-secondary">
              {t('import.check.before.adminsNotAllowed')}
            </p>
          )}
          {admins.map((row) => (
            <div key={row.rowNumber} className="flex items-center justify-between gap-3 border-t border-edge py-2">
              <BlockerPerson person={row.person} />
              <div role="group" aria-label={personDisplayName(row.person)} className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!canMakeAdmin}
                  aria-describedby={canMakeAdmin || loadingPermission ? undefined : notAllowedId}
                  onClick={() => handleAdmin(row.rowNumber, ADMIN_CHOICE.CONFIRM)}
                >
                  {t('import.check.before.confirmAdmin')}
                </Button>
                <Button variant="secondary" size="sm" onClick={() => handleAdmin(row.rowNumber, ADMIN_CHOICE.MEMBER)}>
                  {t('import.check.before.changeToMember')}
                </Button>
              </div>
            </div>
          ))}
        </section>
      ) : null}

      {duplicates.length > 0 ? (
        <section className="flex flex-col gap-1">
          <h3 className="text-sm font-semibold text-content">{t('import.check.before.duplicates', { count: duplicates.length })}</h3>
          <p className="text-xs text-content-secondary">{t('import.check.before.duplicatesHint')}</p>
          {duplicates.map((row) => {
            const issue = row.issues.find((i) => i.code === ISSUE.POSSIBLE_DUPLICATE);
            return <DuplicateCard key={row.rowNumber} row={row} issue={issue} other={otherOf(issue)} onResolve={handleDuplicate} />;
          })}
        </section>
      ) : null}

      {outsideDomain.length + keptOutside.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-content">
            {t('import.check.before.outside', { count: outsideDomain.length + keptOutside.length, domain: catalog.domain })}
          </h3>
          <p className="text-xs text-content-secondary">{t('import.check.before.outsideHint')}</p>
          {outsideDomain.map((row) => (
            <div key={row.rowNumber} className="flex items-center justify-between gap-3 border-t border-edge py-2">
              <BlockerPerson person={row.person} label={t('import.check.before.noAccessYet')} avatar />
              <div role="group" aria-label={personDisplayName(row.person)} className="flex gap-2">
                <Button variant="secondary" size="sm" onClick={() => handleOutsideDomain(row.rowNumber, DOMAIN_CHOICE.KEEP)}>
                  {t('import.check.before.keep')}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleOutsideDomain(row.rowNumber, DOMAIN_CHOICE.REMOVE)}>
                  {t('import.check.before.removeFromList')}
                </Button>
              </div>
            </div>
          ))}
          {keptOutside.map((row) => (
            <div key={row.rowNumber} className="flex items-center justify-between gap-3 border-t border-edge py-2">
              <BlockerPerson person={row.person} label={t('import.check.before.noAccessYet')} avatar />
              <div role="group" aria-label={personDisplayName(row.person)} className="flex items-center gap-2">
                <Badge size="small" variant="success">
                  {t('import.check.before.kept')}
                </Badge>
                <Button variant="ghost" size="sm" onClick={() => handleOutsideDomain(row.rowNumber, DOMAIN_CHOICE.UNDO)}>
                  {t('import.check.before.undo')}
                </Button>
              </div>
            </div>
          ))}
        </section>
      ) : null}
    </Card>
  );
}

export default memo(BeforeYouAddChecklist);
